<script setup lang="ts">
import type { CondoHeroSectionProps } from '.';
import type {
	PhotoButtonEvent,
	PhotoClickEvent,
	SharingOption,
	VideoClickEvent
} from '~/components/ContentShowcaseHero';

import { breakpointsTailwind } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import GmapMarker from '~/core/service/googleMaps/components/GmapMarker/GmapMarker.vue';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isMobileDevice } from '~~/utils';

import {
	MapCondominiumConfig,
	MapCondominiumConfigOptions,
	MapMarkerCondominiumConfig
} from '~/components/condominium';
import { CondoTalkToAgentButton } from '~/components/condominium/CondoTalkToAgentButton';
import { TextLink } from '~/components/ui/text-link';

const props = defineProps<CondoHeroSectionProps>();

const { ORIGIN } = useRuntimeConfig().public;
const emit = defineEmits<{
	(e: 'map-clicked'): void;
	(e: 'photo-clicked'): void;
	(e: 'video-clicked'): void;
	(e: 'address-clicked'): void;
}>();

const condoImages = computed(() => {
	if (!props.condoData) return [];

	return props.condoData.condo.images.map(img => img.url);
});

const condoVideos = computed(() => props.condoData?.condo.videos ?? []);

const imageCount = computed(() => condoImages.value.length);
const hasNoImages = computed(() => imageCount.value === 0);
const hasOneImage = computed(() => imageCount.value === 1);
const hasMoreThanOneImage = computed(() => imageCount.value > 1);
const hasTwoImagesOrMore = computed(() => imageCount.value >= 2);

const formattedAddress = computed(() => {
	if (!props.condoData) return '';
	const { address, number, address2, city, state, region } =
		props.condoData.condo;
	const addressParts = [
		address,
		number,
		address2,
		region,
		city && state ? `${city} - ${state}` : city || state
	].filter(Boolean);

	return addressParts.join(', ');
});

const mapCenter = computed(() => {
	if (props.condoData?.condo.lat && props.condoData?.condo.lng) {
		return {
			lat: props.condoData.condo.lat,
			lng: props.condoData.condo.lng
		};
	}
	return { lat: -23.5835, lng: -46.6792 };
});

const price = computed(() => {
	if (!props.condoData) return 0;
	const { min, max } = props.condoData.propertyStatistics?.priceRange ?? {
		min: 0,
		max: 0
	};

	return min || max;
});

const morePropertiesButtonLabel = computed(() =>
	props.condoData?.propertyStatistics?.totalProperties
		? 'Ver imóveis disponíveis'
		: 'Falar com especialista'
);

function createCondoImageAlt(index: number) {
	return getCondoImageAlt(props.condoData?.condo.name || '', index);
}

const { isSmaller } = useBreakpoints(breakpointsTailwind);

function smoothScroll(targetId: string, offset: number = 140) {
	const targetElement = document.getElementById(targetId);

	if (targetElement) {
		const elementPosition = targetElement.getBoundingClientRect().top;
		const offsetPosition = elementPosition + window.scrollY;

		const navbarOffset = isSmaller('md') ? 105 : offset;

		scrollTo({
			top: offsetPosition - navbarOffset,
			behavior: 'smooth'
		});
	}
}

const {
	trackSharePropertyClicked,
	trackMapPropertyOpened,
	trackModalPhotoOpened,
	trackModalVideoOpened
} = useEvents();

function handleShareClicked(option: SharingOption) {
	trackSharePropertyClicked({
		[MixpanelProps.SHARE_WHATSAPP]: option.label === 'WhatsApp',
		[MixpanelProps.SHARE_FACEBOOK]: option.label === 'Facebook',
		[MixpanelProps.SHARE_EMAIL]: option.label === 'E-mail',
		[MixpanelProps.SHARE_TWITTER]: option.label === 'Twitter',
		[MixpanelProps.COPY_LINK]: false,
		[MixpanelProps.SHARE_CLICKED]: false
	});
}

function handleShareButtonClick() {
	if (isMobileDevice(window?.navigator?.userAgent)) {
		trackSharePropertyClicked({
			[MixpanelProps.SHARE_WHATSAPP]: false,
			[MixpanelProps.SHARE_FACEBOOK]: false,
			[MixpanelProps.SHARE_EMAIL]: false,
			[MixpanelProps.SHARE_TWITTER]: false,
			[MixpanelProps.COPY_LINK]: false,
			[MixpanelProps.SHARE_CLICKED]: true
		});
	}
}

function handleMapClick() {
	emit('map-clicked');
	trackMapPropertyOpened({
		[MixpanelProps.CURRENT_PAGE]: 'Condominio Details',
		[MixpanelProps.CTA_TEXT]: 'Ver mapa',
		[MixpanelProps.SECTION_NAME]: 'hero section',
		[MixpanelProps.CONDO_NAME]: props.condoData?.condo.name || ''
	});
}

function handlePhotosButtonClick({ photoCount }: PhotoButtonEvent) {
	emit('photo-clicked');
	trackModalPhotoOpened({
		[MixpanelProps.CURRENT_PAGE]: 'Condominio Details',
		[MixpanelProps.CLICK_CTA]: true,
		[MixpanelProps.CLICK_PHOTO]: false,
		[MixpanelProps.SECTION_NAME]: 'hero section',
		[MixpanelProps.PHOTO_COUNT]: photoCount
	});
}

function handlePhotoClick({ photoUrl }: PhotoClickEvent) {
	emit('photo-clicked');
	trackModalPhotoOpened({
		[MixpanelProps.CURRENT_PAGE]: 'Condominio Details',
		[MixpanelProps.CLICK_CTA]: false,
		[MixpanelProps.CLICK_PHOTO]: true,
		[MixpanelProps.SECTION_NAME]: 'hero section',
		[MixpanelProps.PHOTO_URL]: photoUrl,
		[MixpanelProps.PHOTO_COUNT]: condoImages.value.length
	});
}

function handleVideoClick({ videoUrl }: VideoClickEvent) {
	emit('video-clicked');
	trackModalVideoOpened({
		[MixpanelProps.CURRENT_PAGE]: 'Condominio Details',
		[MixpanelProps.CLICK_VIDEO]: true,
		[MixpanelProps.CLICK_CTA]: false,
		[MixpanelProps.VIDEO_URL]: videoUrl,
		[MixpanelProps.CONDO_NAME]: props.condoData?.condo.name || ''
	});
}

function handleVideosButtonClick() {
	emit('video-clicked');
	trackModalVideoOpened({
		[MixpanelProps.CURRENT_PAGE]: 'Condominio Details',
		[MixpanelProps.CLICK_VIDEO]: false,
		[MixpanelProps.CLICK_CTA]: true,
		[MixpanelProps.CONDO_NAME]: props.condoData?.condo.name || ''
	});
}

function handleAddressClicked() {
	emit('address-clicked');
}

function handleSeeMoreDescriptionButtonClick() {
	smoothScroll('condo-info-grid', 180);
}

function handleMorePropertiesButtonClick() {
	if (!props.condoData?.exclusiveProperties?.length) {
		smoothScroll('condo-typologies', 180);
		return;
	}

	smoothScroll('exclusive-properties');
}
</script>

<template>
	<ContentShowcaseHero
		:images="condoImages"
		:videos="condoVideos"
		:cta-text="'Ver imóveis disponíveis'"
		:share-url="`${ORIGIN}/condominio/${condoData?.condo.slug}`"
		:is-loading="isLoading"
		:title="condoData?.condo.name || ''"
		price-label="Valores de venda à partir de"
		:price="price"
		:image-alt-generator="createCondoImageAlt"
		first-column-width="wide"
		:show-map-button="hasNoImages || isSmaller('md')"
		button-map-classes="flex md:hidden"
		@share-click="handleShareClicked"
		@share-button-click="handleShareButtonClick"
		@photos-button-click="handlePhotosButtonClick"
		@photo-click="handlePhotoClick"
		@video-click="handleVideoClick"
		@videos-button-click="handleVideosButtonClick"
		@map-click="handleMapClick"
	>
		<template #description>
			<div class="order-3 w-full max-w-[600px] md:order-2">
				<div class="mb-3 mt-1 flex items-center">
					<IconMaterialSymbolsLightLocationOn class="mr-1 size-5 text-beige" />
					<TextLink
						variant="dark"
						as="h2"
						class="text-sm text-white"
						@click="handleAddressClicked"
					>
						{{ formattedAddress }}
					</TextLink>
				</div>

				<div class="flex flex-col gap-3">
					<div class="relative">
						<h2
							class="line-clamp-3 text-base font-normal leading-relaxed text-white transition-all duration-300 ease-in-out"
						>
							{{ condoData?.condo.description }}
						</h2>
					</div>

					<a
						class="flex w-fit cursor-pointer items-center text-sm"
						@click.prevent="handleSeeMoreDescriptionButtonClick"
					>
						<TextLink as="p" variant="dark" class="w-fit">Ver mais</TextLink>
					</a>
				</div>
			</div>
		</template>

		<template #ctaButton>
			<Button
				v-if="condoData?.propertyStatistics?.totalProperties"
				variant="primary-dark"
				class="flex items-center gap-3 px-8 py-3"
				@click.prevent="handleMorePropertiesButtonClick"
			>
				<IconMaterialSymbolsLightVillaOutline class="size-5" />
				<span>Ver imóveis disponíveis</span>
				<span
					class="flex h-6 items-center justify-center rounded-full bg-beige px-2 text-xs font-bold text-primary"
				>
					{{ condoData?.propertyStatistics?.totalProperties || 0 }}
				</span>
			</Button>
			<CondoTalkToAgentButton
				v-else
				section-name="hero section"
				variant="primary-dark"
			/>
		</template>

		<template v-if="hasNoImages" #firstPosition>
			<div class="relative size-full" @click="handleMapClick">
				<ClientOnly fallback-tag="div">
					<template #fallback>
						<div
							class="bg-map-placeholder-image relative size-full bg-cover bg-no-repeat"
						/>
					</template>

					<GmapMap
						:center="mapCenter"
						:zoom-control="false"
						v-bind="MapCondominiumConfig"
						:options="{
							...MapCondominiumConfigOptions,
							center: mapCenter
						}"
						class="size-full"
						:zoom="12"
					>
						<GmapMarker
							:position="mapCenter"
							:options="{
								title: condoData?.condo.name || 'Condomínio',
								...MapMarkerCondominiumConfig
							}"
						/>
					</GmapMap>
				</ClientOnly>
				<HeroButton
					class="absolute bottom-6 left-6 hidden max-w-[98px] text-sm font-semibold md:flex"
					@click="handleMapClick"
				>
					<template #icon>
						<IconMaterialSymbolsLightMapOutline class="size-5 text-beige" />
					</template>
					Mapa
				</HeroButton>
			</div>
		</template>

		<template v-else-if="hasOneImage" #firstPosition>
			<div
				class="relative grid size-full grid-cols-1 gap-1 md:grid-cols-[60%_40%]"
			>
				<div class="relative size-full md:max-h-[500px]">
					<NuxtImg
						:src="condoImages[0]"
						:alt="createCondoImageAlt(0)"
						width="576"
						height="500"
						sizes="xs:375px sm:576px"
						densities="x1"
						class="size-full cursor-pointer object-cover"
						format="webp"
						decoding="async"
						loading="lazy"
						@click="handlePhotoClick({ photoUrl: condoImages[0] })"
					/>
				</div>
				<div
					class="relative hidden size-full max-h-[500px] max-w-[384px] md:block"
					@click="handleMapClick"
				>
					<ClientOnly fallback-tag="div">
						<template #fallback>
							<div
								class="bg-map-placeholder-image relative size-full bg-cover bg-no-repeat"
							/>
						</template>
						<GmapMap
							:center="mapCenter"
							:zoom-control="false"
							v-bind="MapCondominiumConfig"
							:options="{
								...MapCondominiumConfigOptions,
								center: mapCenter
							}"
							class="size-full"
							:zoom="12"
						>
							<GmapMarker
								:position="mapCenter"
								:options="{
									title: condoData?.condo.name || 'Condomínio',
									...MapMarkerCondominiumConfig
								}"
							/>
						</GmapMap>
					</ClientOnly>
					<HeroButton
						class="absolute bottom-6 left-6 hidden max-w-[98px] text-sm font-semibold md:flex"
						@click="handleMapClick"
					>
						<template #icon>
							<IconMaterialSymbolsLightMapOutline class="size-5 text-beige" />
						</template>
						Mapa
					</HeroButton>
				</div>
			</div>
		</template>

		<template v-else #firstPosition>
			<div v-if="isSmaller('md')" class="relative size-full">
				<NuxtImg
					:src="condoImages[0]"
					:alt="createCondoImageAlt(0)"
					width="768"
					height="500"
					sizes="xs:375px sm:640px md:768px"
					densities="x1"
					format="webp"
					class="size-full h-dvh cursor-pointer object-cover"
					decoding="async"
					loading="lazy"
					@click="handlePhotoClick({ photoUrl: condoImages[0], photoIndex: 0 })"
				/>
			</div>
			<div
				v-else
				class="size-full"
				@click="handlePhotoClick({ photoUrl: condoImages[0], photoIndex: 0 })"
			>
				<NuxtImg
					:src="condoImages[0]"
					width="960"
					height="500"
					sizes="md:576px lg:720px xl:960px"
					densities="x1"
					format="webp"
					:alt="createCondoImageAlt(0)"
					class="size-full h-dvh cursor-pointer object-cover"
					decoding="async"
					loading="lazy"
				/>
			</div>
		</template>

		<template v-if="hasTwoImagesOrMore && !isSmaller('md')" #secondPosition>
			<div
				class="relative size-full"
				@click="handlePhotoClick({ photoUrl: condoImages[1], photoIndex: 1 })"
			>
				<NuxtImg
					:src="condoImages[1]"
					width="384"
					height="248"
					sizes="md:320px lg:384px"
					densities="x1"
					format="webp"
					:alt="createCondoImageAlt(1)"
					class="size-full cursor-pointer object-cover"
					decoding="async"
					loading="lazy"
				/>
			</div>
		</template>

		<template v-if="hasMoreThanOneImage && !isSmaller('md')" #thirdPosition>
			<div class="relative size-full" @click="handleMapClick">
				<ClientOnly fallback-tag="div">
					<template #fallback>
						<div
							class="bg-map-placeholder-image relative size-full bg-cover bg-no-repeat"
						/>
					</template>
					<GmapMap
						:center="mapCenter"
						:zoom-control="false"
						v-bind="MapCondominiumConfig"
						:options="{
							...MapCondominiumConfigOptions,
							center: mapCenter,
							cameraControl: false,
							disableDefaultUI: true,
							clickableIcons: false
						}"
						class="h-[240px] md:h-[248px]"
						:zoom="12"
					>
						<GmapMarker
							:position="mapCenter"
							:options="{
								title: condoData?.condo.name || 'Condomínio',
								...MapMarkerCondominiumConfig
							}"
						/>
					</GmapMap>
				</ClientOnly>
			</div>
			<HeroButton
				class="absolute bottom-6 left-6 hidden max-w-[98px] text-sm font-semibold md:flex"
				@click="handleMapClick"
			>
				<template #icon>
					<IconMaterialSymbolsLightMapOutline class="size-5 text-beige" />
				</template>
				Mapa
			</HeroButton>
		</template>
	</ContentShowcaseHero>
</template>
